import cls from 'classnames';
import { useState, useRef } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useFetchDeviceHeatmap } from 'src/model/traps';
import { formatFromISO } from 'src/utils/datefns';
import classes from './PirHeatmap.module.scss';

function cellLevel(count, noData) {
  if (noData) return 'nd';
  if (count === 0) return 'l0';
  if (count <= 2) return 'l1';
  if (count <= 5) return 'l2';
  if (count <= 9) return 'l3';
  return 'l4';
}

function formatCellLabel(date, hour, count, noData) {
  const dow = formatFromISO(date + 'T00:00:00', 'EEE');
  const md = `${date.slice(8)}/${date.slice(5, 7)}`;
  const hh = String(hour).padStart(2, '0');
  if (noData) return { head: `${dow} ${md} · ${hh}:00`, tail: __('no data') };
  return { head: `${dow} ${md} · ${hh}:00`, tail: `${count} ${count === 1 ? __('event') : __('events')}` };
}

const LEVEL_CLASS = {
  l0: '',
  l1: classes.cellL1,
  l2: classes.cellL2,
  l3: classes.cellL3,
  l4: classes.cellL4,
  nd: classes.cellNd,
};

export const PirHeatmap = ({ trap_id, onUnsupported = () => {} }) => {
  const [days, setDays] = useState(14);
  const [periodOffset, setPeriodOffset] = useState(0);
  const [cursor, setCursor] = useState(-1);
  const [tip, setTip] = useState(null); // { content: {head,tail}, x, y } | null

  const chartRef = useRef();
  const tipRef = useRef();
  const gridRef = useRef();

  const { data: heatmapData, error: heatmapError } = useFetchDeviceHeatmap({
    id: trap_id, days, offset: periodOffset,
  });

  const cells = heatmapData
    ? heatmapData.days.flatMap(dayData =>
        dayData.hours.map(h => ({
          date: dayData.date,
          hour: h.hour,
          count: h.count,
          noData: h.no_data,
        }))
      )
    : [];

  const dates = heatmapData ? heatmapData.days.map(d => d.date) : [];

  const subtitle = !heatmapData
    ? ''
    : periodOffset === 0
      ? `${days} ${__('days (most recent)')}`
      : (() => {
          const { from, to } = heatmapData.range;
          const fmtD = s => `${s.slice(8)}/${s.slice(5, 7)}`;
          const fromYear = from.slice(0, 4);
          const toYear = to.slice(0, 4);
          const fromStr = fromYear !== toYear ? `${fmtD(from)} ${fromYear}` : fmtD(from);
          return `${fromStr} \u2013 ${fmtD(to)} ${toYear}`;
        })();

  // ── controls ─────────────────────────────────────────────────────────────

  function handleRangeSelect(d) {
    setDays(d);
    setPeriodOffset(0);
    setCursor(-1);
    setTip(null);
  }

  function handlePrev() {
    setPeriodOffset(o => o + 1);
    setCursor(-1);
    setTip(null);
  }

  function handleNext() {
    if (periodOffset > 0) {
      setPeriodOffset(o => o - 1);
      setCursor(-1);
      setTip(null);
    }
  }

  // ── tooltip ───────────────────────────────────────────────────────────────

  function showTipForIndex(idx) {
    const cell = cells[idx];
    if (!cell || !chartRef.current || !gridRef.current) return;
    const cellEl = gridRef.current.children[idx];
    if (!cellEl) return;
    const chartRect = chartRef.current.getBoundingClientRect();
    const cellRect = cellEl.getBoundingClientRect();
    const halfTip = tipRef.current ? tipRef.current.offsetWidth / 2 : 70;
    const x = Math.min(
      Math.max(cellRect.left - chartRect.left + cellRect.width / 2, halfTip + 4),
      chartRect.width - halfTip - 4
    );
    const y = cellRect.top - chartRect.top;
    setTip({ content: formatCellLabel(cell.date, cell.hour, cell.count, cell.noData), x, y });
  }

  function handlePointerOver(e) {
    const cellEl = e.target.closest('[data-ci]');
    if (!cellEl) return;
    const idx = Number(cellEl.dataset.ci);
    showTipForIndex(idx);
  }

  function handlePointerLeave() { setTip(null); }

  // ── keyboard ──────────────────────────────────────────────────────────────

  function handleKeyDown(e) {
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 24, ArrowUp: -24 }[e.key];
    if (step === undefined) return;
    e.preventDefault();
    const next = Math.max(0, Math.min(cells.length - 1, cursor < 0 ? 0 : cursor + step));
    setCursor(next);
    showTipForIndex(next);
  }

  function handleGridBlur() {
    setCursor(-1);
    setTip(null);
  }

  // ── render ────────────────────────────────────────────────────────────────

  if (heatmapError) {
    if (heatmapError.code === 'heatmap_not_supported') {
      onUnsupported();
      return null;
    }
    return (
      <div className={classes.wrapper}>
        <Alert variant="danger">
          {heatmapError.text || __('Could not load activity heatmap.')}
        </Alert>
      </div>
    );
  }

  const isLoading = heatmapData === null;

  return (
    <div className={classes.wrapper}>

      {/* ── header: subtitle + range pills ── */}
      <div className={classes.header}>
        <p className={classes.subtitle}>{subtitle}</p>
        <div className={classes.rangeGroup} role="group" aria-label={__('Time range')}>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              type="button"
              className={cls(classes.pill, { [classes.pillActive]: days === d })}
              aria-pressed={days === d}
              onClick={() => handleRangeSelect(d)}
            >
              {d}{__('d')}
            </button>
          ))}
        </div>
      </div>

      {/* ── chart ── */}
      {isLoading
        ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )
        : (
          <div className={classes.chart} ref={chartRef}>

            {/* hour axis */}
            <div className={classes.axisRow}>
              <div />
              <div className={classes.hoursAxis} aria-hidden="true">
                {[0, 3, 6, 9, 12, 15, 18, 21].map(h => (
                  <span key={h} style={{ gridColumn: h + 1 }}>{h}</span>
                ))}
              </div>
            </div>

            {/* day axis + grid */}
            <div className={classes.axisRow}>
              <div className={classes.daysAxis} aria-hidden="true">
                {dates.map(date => (
                  <span key={date}>{`${date.slice(8)}/${date.slice(5, 7)}`}</span>
                ))}
              </div>

              <div
                ref={gridRef}
                className={classes.grid}
                tabIndex={0}
                role="img"
                aria-label={__('Motion events per hour. Use arrow keys to inspect cells.')}
                onPointerOver={handlePointerOver}
                onPointerLeave={handlePointerLeave}
                onKeyDown={handleKeyDown}
                onBlur={handleGridBlur}
              >
                {cells.map((cell, idx) => {
                  const lv = cellLevel(cell.count, cell.noData);
                  return (
                    <div
                      key={`${cell.date}-${cell.hour}`}
                      data-ci={idx}
                      className={cls(
                        classes.cell,
                        LEVEL_CLASS[lv],
                        { [classes.cellCursor]: cursor === idx }
                      )}
                    />
                  );
                })}
              </div>
            </div>

            {/* tooltip */}
            {tip && (
              <div
                ref={tipRef}
                className={classes.tooltip}
                role="status"
                aria-live="polite"
                style={{ left: tip.x, top: tip.y }}
              >
                <b>{tip.content.tail}</b><br />
                <em>{tip.content.head}</em>
              </div>
            )}
          </div>
        )
      }

      {/* ── footer: legend + navigation ── */}
      <div className={classes.footer}>
        <div className={classes.legend}>
          <div className={classes.scale}>
            <span>{__('Less')}</span>
            {['swatchL0','swatchL1','swatchL2','swatchL3','swatchL4'].map(s => (
              <span key={s} className={cls(classes.swatch, classes[s])} />
            ))}
            <span>{__('More')}</span>
          </div>
          <span>{__('0 · 1–2 · 3–5 · 6–9 · 10+ /hr')}</span>
          <span className={classes.ndKey}>
            <span className={cls(classes.swatch, classes.swatchNd)} />
            {__('No data')}
          </span>
        </div>

        <div className={classes.navGroup}>
          <button type="button" className={classes.navBtn} onClick={handlePrev}>
            ← {__('Previous period')}
          </button>
          <button type="button" className={classes.navBtn} disabled={periodOffset === 0} onClick={handleNext}>
            {__('Next period')} →
          </button>
        </div>
      </div>

    </div>
  );
};
